import { useMemo } from 'react'
import { CheckCheck } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

const PLACEHOLDER_RE = /\{\{(\d+)\}\}/g

export interface TemplateWhatsAppPreviewProps {
  body: string
  variables: string[]
  category?: string
  language?: string
  className?: string
}

interface Segment {
  type: 'text' | 'placeholder' | 'newline'
  value: string
  index?: number
  filled?: boolean
}

function parseSegments(body: string, variables: string[]): Segment[] {
  if (body.length === 0) return []

  const segments: Segment[] = []
  let cursor = 0

  const matches = [...body.matchAll(PLACEHOLDER_RE)]
  for (const m of matches) {
    const start = m.index ?? 0
    const raw = m[0]
    const rawIdx = m[1]
    if (rawIdx === undefined) continue
    const idx = Number.parseInt(rawIdx, 10)
    if (!Number.isFinite(idx) || idx <= 0) continue

    if (start > cursor) {
      pushTextWithBreaks(segments, body.slice(cursor, start))
    }

    const varName = variables[idx - 1]
    const filled = varName !== undefined && varName.trim() !== ''
    segments.push({
      type: 'placeholder',
      value: filled ? varName : `{{${idx}}}`,
      index: idx,
      filled,
    })

    cursor = start + raw.length
  }

  if (cursor < body.length) {
    pushTextWithBreaks(segments, body.slice(cursor))
  }

  return segments
}

function pushTextWithBreaks(segments: Segment[], chunk: string): void {
  const parts = chunk.split('\n')
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i] ?? ''
    if (part.length > 0) {
      segments.push({ type: 'text', value: part })
    }
    if (i < parts.length - 1) {
      segments.push({ type: 'newline', value: '' })
    }
  }
}

function nowLabel(): string {
  return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

export function TemplateWhatsAppPreview({
  body,
  variables,
  category,
  language,
  className,
}: TemplateWhatsAppPreviewProps): React.ReactElement {
  const segments = useMemo(() => parseSegments(body, variables), [body, variables])
  const hasContent = segments.length > 0
  const time = useMemo(() => nowLabel(), [])

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium">Preview WhatsApp</p>
        {category !== undefined && <Badge variant="outline">{category}</Badge>}
        {language !== undefined && <Badge variant="outline">{language}</Badge>}
      </div>

      {/* WhatsApp chat background — paper-style */}
      <div
        role="region"
        aria-label="Pré-visualização do template no WhatsApp"
        className="rounded-md border bg-[#e5ddd5] p-4 dark:bg-[#0b141a]"
      >
        {!hasContent && (
          <div className="rounded-md bg-white/70 p-4 text-center text-xs text-muted-foreground dark:bg-white/5">
            Sem conteúdo. Preencha o corpo do template para ver o preview.
          </div>
        )}
        {hasContent && (
          <div className="flex justify-end">
            <div className="relative max-w-[80%] rounded-lg rounded-tr-none bg-[#d9fdd3] px-3 py-2 text-sm text-zinc-900 shadow-sm dark:bg-[#005c4b] dark:text-zinc-50">
              <p className="whitespace-pre-wrap break-words leading-snug">
                {segments.map((seg, i) => {
                  if (seg.type === 'newline') {
                    return <br key={i} />
                  }
                  if (seg.type === 'placeholder') {
                    return (
                      <span
                        key={i}
                        className={cn(
                          'rounded px-1 font-medium',
                          seg.filled
                            ? 'bg-emerald-500/20 text-emerald-900 dark:text-emerald-100'
                            : 'bg-amber-400/30 text-amber-900 dark:text-amber-100',
                        )}
                        title={
                          seg.filled
                            ? `Variável ${seg.index ?? '?'} preenchida`
                            : `Variável ${seg.index ?? '?'} sem nome`
                        }
                      >
                        {seg.value}
                      </span>
                    )
                  }
                  return <span key={i}>{seg.value}</span>
                })}
              </p>
              <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-zinc-500 dark:text-zinc-300">
                <span>{time}</span>
                <CheckCheck className="h-3 w-3" aria-hidden="true" />
              </div>
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Trechos em <span className="rounded bg-emerald-500/20 px-1">verde</span> são variáveis
        nomeadas; em <span className="rounded bg-amber-400/30 px-1">âmbar</span> ainda precisam
        de nome.
      </p>
    </div>
  )
}

export default TemplateWhatsAppPreview
